import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import { BaseUrl } from "../../helper/BaseUrl";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

export default function NewsEdit() {
  const navigate = useNavigate();
  const { news_id } = useParams();
  const [categories, setCategories] = useState([]);
  const [postData, setPostData] = useState({});
  const [previews, setPreviews] = useState({ thumbnail_image: null, banner_image: null });

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await ApiRequest("GET", `${ApiEndpoints.news.newsEdit}${news_id}`);
        const news = response.news || {};
        setPostData({
          media_category_id: news.media_category_id || "",
          title: news.title || "",
          slug: news.slug || "",
          source: news.source || "",
          source_url: news.source_url || "",
          news_type: news.news_type || "",
          short_description: news.short_description || "",
          description: news.description || "",
          date: news.date ? new Date(news.date).toISOString().split("T")[0] : "",
          status: news.status || "0",
        });
        setCategories(response.categories || []);
        setPreviews({
          thumbnail_image: news.thumbnail_image ? BaseUrl + news.thumbnail_image : null,
          banner_image: news.banner_image ? BaseUrl + news.banner_image : null,
        });
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to fetch news.");
      }
    };
    fetchNews();
  }, [news_id]);

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    if (files) {
      const file = files[0];
      if (!file) return;
      setPreviews((previous) => ({ ...previous, [name]: URL.createObjectURL(file) }));
      setPostData((previous) => ({ ...previous, [name]: file }));
      return;
    }
    setPostData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const requiredFields = ["media_category_id", "title", "slug", "source", "source_url", "news_type", "short_description", "description", "date"];
    const missingField = requiredFields.find((field) => !postData[field]);
    if (missingField) {
      toast.error(`The field "${missingField}" is mandatory.`);
      return;
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => formData.append(key, value));
    try {
      const response = await ApiRequest("POST", `${ApiEndpoints.news.newsUpdate}${news_id}`, formData);
      toast.success(response.message);
      navigate("/news");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update news.");
    }
  };

  const pagename = {
    name: "Media Management",
    user: "Admin",
    innerpage: "Media Management / News / Edit News",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="newsEdit" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Title<small>*</small></label></div>
                  <input name="title" value={postData.title || ""} onChange={handleChange} type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Slug<small>*</small></label></div>
                  <input name="slug" value={postData.slug || ""} onChange={handleChange} type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Media Category<small>*</small></label></div>
                  <CustomSelect name="media_category_id" options={categories} defaultValue={postData.media_category_id} onChange={handleChange} placeholder="Select Media Category" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Date<small>*</small></label></div>
                  <input name="date" value={postData.date || ""} onChange={handleChange} type="date" className="form-control" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Source<small>*</small></label></div>
                  <input name="source" value={postData.source || ""} onChange={handleChange} type="text" className="form-control" placeholder="Enter Source Name" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Source URL<small>*</small></label></div>
                  <input name="source_url" value={postData.source_url || ""} onChange={handleChange} type="url" className="form-control" placeholder="https://example.com/article" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>News Type<small>*</small></label></div>
                  <input name="news_type" value={postData.news_type || ""} onChange={handleChange} type="text" className="form-control" placeholder="Enter News Type" />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Short Description<small>*</small></label></div>
                  <textarea name="short_description" value={postData.short_description || ""} onChange={handleChange} className="form-control" rows="4" placeholder="Enter Short Description" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Description<small>*</small></label></div>
                  <DynamicCKEditor data={postData.description || ""} onChange={(event, editor) => setPostData((previous) => ({ ...previous, description: editor.getData() }))} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add"><label>Banner Image (1200 × 580)<small>*</small></label></div>
                  <CustomImageUpload name="banner_image" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.banner_image} />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add"><label>Thumbnail Image (468 × 340)<small>*</small></label></div>
                  <CustomImageUpload name="thumbnail_image" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.thumbnail_image} />
                </div>
              </div>
            </div>
          </div>
        </form>
        <button type="submit" form="newsEdit" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
